import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { ToastProvider } from './components/ui/ToastProvider';
import { ApiError } from './api/client';
import { configProblems, isConfigured } from './config';
import { SetupNeeded } from './SetupNeeded';
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The socket tells us when data changed, so polling on window focus is
      // redundant. Reconnecting the tab already triggers a refetch.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Retrying a 401 or a 409 just repeats the same answer.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');

/*
 * Without configuration, nothing below the shell can work — signing in needs a
 * user pool. So it is checked before the providers mount rather than handled
 * inside them.
 */
createRoot(root).render(
  <StrictMode>
    {isConfigured ? (
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    ) : (
      <SetupNeeded problems={configProblems} />
    )}
  </StrictMode>
);
